import { Plus, Trash2, Upload, X, FileCheck, Users, HeartHandshake } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Checkbox } from "@project/components/ui/checkbox";
import { Badge } from "@project/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { projectToast } from "@project/lib/toast";

type FamilyMemberRow = {
  name: string;
  age: string;
  relationship: string;
  occupation: string;
  mobile_number: string;
};

type TiffinSevaFormFieldsProps = {
  editing: Record<string, any> | null;
  setEditing: React.Dispatch<React.SetStateAction<Record<string, any> | null>>;
};

export function TiffinSevaFormFields({
  editing,
  setEditing,
}: TiffinSevaFormFieldsProps) {
  const family = Array.isArray(editing?.family_members)
    ? (editing.family_members as FamilyMemberRow[])
    : [];

  const setField = (key: string, value: any) => {
    setEditing(prev => ({
      ...(prev ?? {}),
      [key]: value,
    }));
  };

  const updateFamilyMember = (index: number, key: keyof FamilyMemberRow, value: string) => {
    const updated = [...family];
    updated[index] = { ...updated[index], [key]: value };
    setField("family_members", updated);
  };

  const addFamilyMember = () => {
    const newMember: FamilyMemberRow = {
      name: "",
      age: "",
      relationship: "",
      occupation: "",
      mobile_number: "",
    };
    setField("family_members", [...family, newMember]);
  };

  const removeFamilyMember = (index: number) => {
    const updated = family.filter((_, i) => i !== index);
    setField("family_members", updated);
  };

  // Helper for identity checklist options
  const checklistOptions = [
    { value: "Aadhaar Card", label: "Aadhaar Card" },
    { value: "Ration Card", label: "Ration Card" },
    { value: "Voter ID Card", label: "Voter ID Card" },
  ];

  const currentDocs = Array.isArray(editing?.identity_documents)
    ? (editing.identity_documents as string[])
    : [];

  const toggleDoc = (value: string, checked: boolean) => {
    if (checked) {
      setField("identity_documents", [...currentDocs, value]);
    } else {
      setField("identity_documents", currentDocs.filter(d => d !== value));
    }
  };

  // Step 1: Category Selection popup view for new offline applications
  if (!editing?.id && (editing?.step === "category" || !editing?.categorys || editing?.categorys === "-")) {
    return (
      <div className="py-6 space-y-6 max-h-[70vh] overflow-y-auto pr-1">
        <div className="text-center max-w-lg mx-auto space-y-1.5">
          <h3 className="text-lg font-bold text-foreground">Select Age Category</h3>
          <p className="text-xs text-muted-foreground">
            Select the appropriate category for the new offline registration.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Option 1: Above 60 Years */}
          <div
            onClick={() => {
              setEditing(prev => ({
                ...(prev ?? {}),
                categorys: "Above 60 Years",
                seva_category: "FREE",
                payment_status: "FREE",
                age: "65",
                step: "form",
              }));
            }}
            className="group relative cursor-pointer rounded-2xl border-2 border-border p-5 transition-all duration-200 hover:border-primary hover:bg-primary/[0.03] hover:shadow-md"
          >
            <div className="flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700 group-hover:scale-105 transition-transform">
                <Users className="h-6 w-6" />
              </div>
              <Badge className="bg-emerald-600 text-white font-bold text-[11px] px-2.5 py-0.5">
                Senior Citizen
              </Badge>
            </div>
            <div className="mt-4 space-y-1.5">
              <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors">
                Above 60 Years
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Direct trust-sponsored free satvik meal delivery program.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
              <span className="font-semibold text-emerald-700">Free Seva</span>
              <span className="text-[11px] text-muted-foreground font-medium">No payment needed</span>
            </div>
          </div>

          {/* Option 2: Below 60 Years */}
          <div
            onClick={() => {
              setEditing(prev => ({
                ...(prev ?? {}),
                categorys: "Below 60 Years",
                seva_category: "PAID",
                payment_status: "PAID",
                age: "45",
                step: "form",
              }));
            }}
            className="group relative cursor-pointer rounded-2xl border-2 border-border p-5 transition-all duration-200 hover:border-primary hover:bg-primary/[0.03] hover:shadow-md"
          >
            <div className="flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-700 group-hover:scale-105 transition-transform">
                <HeartHandshake className="h-6 w-6" />
              </div>
              <Badge className="bg-blue-600 text-white font-extrabold text-[11px] px-2.5 py-0.5">
                General Public
              </Badge>
            </div>
            <div className="mt-4 space-y-1.5">
              <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors">
                Below 60 Years
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Paid lunch & dinner delivery service at subsidized rates.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
              <span className="font-extrabold text-blue-700">₹100 / Month</span>
              <span className="text-[11px] text-muted-foreground font-medium">Registration fee applies</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: The actual form fields step
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
      {/* Category Banner & Switcher (only for new additions) */}
      {!editing?.id && (
        <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-border bg-secondary/30">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setField("step", "category")}
              className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1 px-1.5"
            >
              ← Change Category
            </Button>
            <span className="text-xs text-muted-foreground">|</span>
            <span className="text-xs font-bold text-foreground">
              Category: {editing?.categorys}
            </span>
          </div>
        </div>
      )}

      {/* SECTION 1: APPLICANT DETAILS */}
      <div className="space-y-4 rounded-xl border border-border/80 bg-card p-4">
        <h4 className="text-sm font-bold text-foreground border-b border-border pb-2">
          1. Applicant Details
        </h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-semibold">Tiffin Start Date *</Label>
            <Input
              type="date"
              required
              value={editing?.tiffin_start_date ?? ""}
              onChange={(e) => setField("tiffin_start_date", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Tiffin End Date</Label>
            <Input
              type="date"
              value={editing?.tiffin_end_date ?? ""}
              onChange={(e) => setField("tiffin_end_date", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold">Full Name *</Label>
            <Input
              required
              placeholder="Enter full name"
              value={editing?.name ?? ""}
              onChange={(e) => setField("name", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold">Address *</Label>
            <Textarea
              required
              rows={2}
              placeholder="Enter address details"
              value={editing?.address ?? ""}
              onChange={(e) => setField("address", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Age *</Label>
            <Input
              type="number"
              required
              min="1"
              placeholder="Age"
              value={editing?.age ?? ""}
              onChange={(e) => setField("age", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Gender *</Label>
            <Select
              value={editing?.gender ?? ""}
              onValueChange={(val) => setField("gender", val)}
            >
              <SelectTrigger className="mt-1 text-xs h-9">
                <SelectValue placeholder="Select Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Date of Birth *</Label>
            <Input
              type="date"
              required
              value={editing?.date_of_birth ?? ""}
              onChange={(e) => setField("date_of_birth", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Mobile Number *</Label>
            <Input
              type="tel"
              required
              placeholder="98250 12345"
              value={editing?.mobile_number ?? ""}
              onChange={(e) => setField("mobile_number", e.target.value)}
              className="mt-1 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Category *</Label>
            <Select
              value={editing?.categorys ?? ""}
              onValueChange={(val) => setField("categorys", val)}
            >
              <SelectTrigger className="mt-1 text-xs h-9">
                <SelectValue placeholder="Select Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Above 60 Years">Above 60 Years</SelectItem>
                <SelectItem value="Below 60 Years">Below 60 Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Seva Category *</Label>
            <Select
              value={editing?.seva_category ?? ""}
              onValueChange={(val) => setField("seva_category", val)}
            >
              <SelectTrigger className="mt-1 text-xs h-9">
                <SelectValue placeholder="Select Seva Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FREE">FREE</SelectItem>
                <SelectItem value="PAID">PAID</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {editing?.categorys === "Below 60 Years" && (
            <div>
              <Label className="text-xs font-semibold">Tiffin Seva Type *</Label>
              <Select
                value={editing?.tiffin_type ?? "Daily"}
                onValueChange={(val) => setField("tiffin_type", val)}
              >
                <SelectTrigger className="mt-1 text-xs h-9">
                  <SelectValue placeholder="Select Seva Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Daily">Daily</SelectItem>
                  <SelectItem value="One-Time">One-Time</SelectItem>
                  <SelectItem value="Weekly">Weekly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {editing?.categorys !== "Below 60 Years" && (
            <>
              <div>
                <Label className="text-xs font-semibold">Monthly Income</Label>
                <Input
                  placeholder="e.g. ₹10,000 / month"
                  value={editing?.monthly_income ?? ""}
                  onChange={(e) => setField("monthly_income", e.target.value)}
                  className="mt-1 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Tiffin Recipient</Label>
                <Input
                  placeholder="e.g. Self, Spouse"
                  value={editing?.tiffin_recipient ?? "Self"}
                  onChange={(e) => setField("tiffin_recipient", e.target.value)}
                  className="mt-1 text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Tiffin Arrangement Details</Label>
                <Input
                  placeholder="Lunch & Dinner Delivery details"
                  value={editing?.tiffin_arrangement_details ?? "Lunch & Dinner Delivery"}
                  onChange={(e) => setField("tiffin_arrangement_details", e.target.value)}
                  className="mt-1 text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Reason for Providing Tiffin *</Label>
                <Textarea
                  required
                  rows={2}
                  placeholder="Describe the reason/necessity"
                  value={editing?.reason_for_providing_tiffin ?? ""}
                  onChange={(e) => setField("reason_for_providing_tiffin", e.target.value)}
                  className="mt-1 text-xs"
                />
              </div>
            </>
          )}
        </div>
      </div>
      {/* SECTION 2: FAMILY MEMBERS */}
      {editing?.categorys !== "Below 60 Years" && (
        <div className="space-y-4 rounded-xl border border-border/80 bg-card p-4">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <h4 className="text-sm font-bold text-foreground">
              2. Family Members Details
            </h4>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addFamilyMember}
              className="text-xs h-7 px-2"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Member
            </Button>
          </div>
          
          {family.length === 0 ? (
            <p className="text-xs text-muted-foreground italic text-center py-2">
              No family members added.
            </p>
          ) : (
            <div className="space-y-3">
              {family.map((member, idx) => (
                <div key={idx} className="grid gap-3 sm:grid-cols-5 p-3 rounded-lg border border-border bg-secondary/10 relative">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeFamilyMember(idx)}
                    className="absolute right-1 top-1 h-6 w-6 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] font-semibold">Name</Label>
                    <Input
                      placeholder="Name"
                      value={member.name}
                      onChange={(e) => updateFamilyMember(idx, "name", e.target.value)}
                      className="h-8 text-xs mt-0.5"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold">Age</Label>
                    <Input
                      type="number"
                      placeholder="Age"
                      value={member.age}
                      onChange={(e) => updateFamilyMember(idx, "age", e.target.value)}
                      className="h-8 text-xs mt-0.5"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold">Relationship</Label>
                    <Input
                      placeholder="e.g. Son"
                      value={member.relationship}
                      onChange={(e) => updateFamilyMember(idx, "relationship", e.target.value)}
                      className="h-8 text-xs mt-0.5"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold">Occupation & Phone</Label>
                    <Input
                      placeholder="e.g. Engineer"
                      value={member.occupation}
                      onChange={(e) => updateFamilyMember(idx, "occupation", e.target.value)}
                      className="h-8 text-xs mt-0.5"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SECTION 3: REFERENCE PROVIDER DETAILS */}
      {editing?.categorys !== "Below 60 Years" && (
        <div className="space-y-4 rounded-xl border border-border/80 bg-card p-4">
          <h4 className="text-sm font-bold text-foreground border-b border-border pb-2">
            3. Reference Provider Details
          </h4>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold">Reference Name</Label>
              <Input
                placeholder="Dr. Sharma"
                value={editing?.reference_provider_name ?? ""}
                onChange={(e) => setField("reference_provider_name", e.target.value)}
                className="mt-1 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Reference Mobile</Label>
              <Input
                type="tel"
                placeholder="98980 12345"
                value={editing?.reference_provider_mobile ?? ""}
                onChange={(e) => setField("reference_provider_mobile", e.target.value)}
                className="mt-1 text-xs font-mono"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold">Reference Address</Label>
              <Input
                placeholder="Hospital/Clinic address"
                value={editing?.reference_provider_address ?? ""}
                onChange={(e) => setField("reference_provider_address", e.target.value)}
                className="mt-1 text-xs"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold">Reference Occupation</Label>
              <Input
                placeholder="Doctor, Social Worker"
                value={editing?.reference_provider_occupation ?? ""}
                onChange={(e) => setField("reference_provider_occupation", e.target.value)}
                className="mt-1 text-xs"
              />
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4: IDENTITY DOCUMENTS */}
      <div className="space-y-4 rounded-xl border border-border/80 bg-card p-4">
        <h4 className="text-sm font-bold text-foreground border-b border-border pb-2">
          4. Proof of Identity
        </h4>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-muted-foreground block mb-2">
              Select Identity Documents
            </Label>
            <div className="flex flex-wrap gap-4">
              {checklistOptions.map(option => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                  <Checkbox
                    checked={currentDocs.includes(option.value)}
                    onCheckedChange={(checked) => toggleDoc(option.value, Boolean(checked))}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <Label className="text-xs font-semibold block mb-2">Upload Identity Document</Label>
            <div className="flex items-center gap-3">
              <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:border-primary/60 hover:bg-primary/[0.02] transition-colors">
                <Upload className="h-4 w-4 text-primary" />
                <span>
                  {editing?.identity_document_file
                    ? "Change File"
                    : "Choose File (PDF, JPG, PNG)"}
                </span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setField("identity_document_file", file);
                      projectToast.success(`Selected file: ${file.name}`);
                    }
                  }}
                />
              </label>

              {(editing?.identity_document_file || editing?.identity_document) && (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-800">
                  <FileCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="truncate max-w-[150px] font-medium">
                    {editing?.identity_document_file
                      ? editing.identity_document_file.name
                      : "Uploaded Document"}
                  </span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setField("identity_document_file", null);
                      setField("identity_document", null);
                    }}
                    className="h-5 w-5 text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 5: FEE & PAYMENT STATUS */}
      <div className="space-y-4 rounded-xl border border-border/80 bg-card p-4">
        <h4 className="text-sm font-bold text-foreground border-b border-border pb-2">
          5. Fee & Payment Status
        </h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-semibold">Fee Amount *</Label>
            <Input
              type="number"
              placeholder="100"
              value={editing?.fee_amount ?? ""}
              onChange={(e) => setField("fee_amount", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Payment Status *</Label>
            <Select
              value={editing?.payment_status ?? ""}
              onValueChange={(val) => setField("payment_status", val)}
            >
              <SelectTrigger className="mt-1 text-xs h-9">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAID">PAID</SelectItem>
                <SelectItem value="PENDING">PENDING</SelectItem>
                <SelectItem value="FREE">FREE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Payment Mode</Label>
            <Select
              value={editing?.payment_mode ?? ""}
              onValueChange={(val) => setField("payment_mode", val)}
            >
              <SelectTrigger className="mt-1 text-xs h-9">
                <SelectValue placeholder="Select Payment Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">CASH</SelectItem>
                <SelectItem value="UPI">UPI</SelectItem>
                <SelectItem value="BANK_TRANSFER">BANK_TRANSFER</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Payment Reference</Label>
            <Input
              placeholder="e.g. Transaction ID, Receipt No"
              value={editing?.payment_reference ?? ""}
              onChange={(e) => setField("payment_reference", e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
